/**
 * Prints an HTML fragment via a hidden iframe — no popup flicker, no new tab.
 * Extracted from the DIN-label print on PatientProcedurePage so the newer
 * product-label print (richer markup, same mechanism) doesn't duplicate it.
 */
export function printLabelViaIframe(title: string, bodyHtml: string, styleCss: string): void {
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  document.body.appendChild(iframe)

  const doc = iframe.contentWindow?.document
  if (!doc) {
    iframe.remove()
    return
  }
  doc.open()
  doc.write(
    `<!doctype html><html><head><title>${escapeHtml(title)}</title>` +
      `<style>${styleCss}</style></head><body>${bodyHtml}</body></html>`,
  )
  doc.close()

  const win = iframe.contentWindow
  if (!win) {
    iframe.remove()
    return
  }
  win.onafterprint = () => iframe.remove()
  win.focus()
  win.print()
}

/**
 * Print several labels as SEPARATE print jobs, one after another.
 *
 * Deliberately not one document containing every label: a label printer has
 * to see each as its own job, or a batch comes out as a single long sheet
 * that then has to be cut by hand. One job per label is also what makes a
 * failed label reprintable on its own.
 *
 * The jobs are chained rather than fired in a loop because a browser runs one
 * print dialog at a time — a loop would collapse into a single dialog. Each
 * step resolves on `afterprint`, with a short timer as a fallback: that event
 * does not fire in every browser, and does not fire at all if the operator
 * dismisses the dialog, and a batch that stalls half-way would be worse than
 * one that advances slightly early.
 */
export async function printLabelsSequentially(
  labels: { title: string; bodyHtml: string }[],
  styleCss: string,
  onProgress?: (printed: number, total: number) => void,
): Promise<void> {
  for (let i = 0; i < labels.length; i++) {
    onProgress?.(i, labels.length)
    await printOne(labels[i].title, labels[i].bodyHtml, styleCss)
  }
  onProgress?.(labels.length, labels.length)
}

/** One label, one job — resolves once the print dialog is done with it. */
function printOne(title: string, bodyHtml: string, styleCss: string): Promise<void> {
  return new Promise(resolve => {
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
    document.body.appendChild(iframe)

    const win = iframe.contentWindow
    const doc = win?.document
    if (!win || !doc) {
      iframe.remove()
      resolve()
      return
    }

    doc.open()
    doc.write(
      `<!doctype html><html><head><title>${escapeHtml(title)}</title>` +
        `<style>${styleCss}</style></head><body>${bodyHtml}</body></html>`,
    )
    doc.close()

    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      iframe.remove()
      resolve()
    }

    win.onafterprint = finish
    win.focus()
    win.print()
    // `print()` blocks until the dialog closes in Chromium and Firefox, so by
    // here the job is usually already away; this covers the browsers where
    // `afterprint` never arrives.
    setTimeout(finish, 250)
  })
}

export function escapeHtml(v: string): string {
  return v.replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c] ?? c)
}
